/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { TeamInRegion, TeamsInRegion } from "@microsoft/shifts-models-data";
// TODO: Replace 'sh-services' dependency from Web core with one from a workspace package.
import { NetworkService } from "sh-services";
import { DataProvider } from "sh-services/dataproviders/DataProvider";

import { CrossLocationTeamDataProviderParams } from "./CrossLocationTeamDataProviderParams";

/**
 * The cross-location team data provider.
 */
export class CrossLocationTeamDataProviderV2 extends DataProvider<TeamsInRegion> {
    /**
     * Instantiates a new instance of the CrossLocationTeamDataProvider.
     * @param params The parameters.
     */
    constructor(private readonly params: CrossLocationTeamDataProviderParams) {
        super();
    }

    /**
     * Gets data from memory or undefined.
     * @returns The data if it's found in memory.
     */
    public async getDataFromMemory(): Promise<TeamsInRegion | undefined> {
        // Memory only stores view models, so returning undefined.
        return Promise.resolve(undefined);
    }

    /**
     * Return data if it's found in the database (otherwise return undefined).
     * @returns The data if it's found in database.
     */
    public async getDataFromDatabase(): Promise<TeamsInRegion> {
        const { database, teamId } = this.params;
        const teams = await database.getCrossLocationTeams(teamId);

        return teams;
    }

    /**
     * Gets data from network call.
     * @returns The data from the network call.
     */
    public async getDataFromNetwork(): Promise<TeamsInRegion> {

        const { teamId } = this.params;
        const url = `/api/teams/${teamId}/crosslocation/publishfrom`;
        const urlSignature = "/api/teams/{teamId}/crosslocation/publishfrom";

        const { data: {teams} } = await NetworkService.sendHttpRequestToStaffHubService<
        {
            /**
             * The teams in the region.
             */
            teams:
            {
                /**
                 * Whether cross-location shifts feature is enabled for the team.
                 */
                crossLocationShiftsEnabled: boolean;
                /**
                 * Team id.
                 */
                teamId: string;
                /**
                 * Team name.
                 */
                name: string;
                /**
                 * AAD group id .
                 */
                groupId?: string;
            }[]
        }
        >({
            method: "GET",
            url,
            urlSignature
        });

        return teams?.filter(({ crossLocationShiftsEnabled }) => crossLocationShiftsEnabled).map(
            ({teamId, name, groupId, crossLocationShiftsEnabled}) =>
                ({id: teamId, name, groupId, crossLocationShiftsEnabled } as TeamInRegion)) ?? [];
    }

    /**
     * Sets data in memory.
     * @param data The data to set in memory.
     */
    public async setDataInMemory(data: TeamsInRegion): Promise<void> {
        this.params.setDataInMemory(data);
    }

    /**
     * Sets data in database.
     * @param data The data to set in database.
     */
    public async setDataInDatabase(data?: TeamsInRegion): Promise<void> {
        if (data) {
            const { database, teamId } = this.params;

            await database.setCrossLocationTeams(teamId, data);
        }
    }

    /**
     * Not supported.
     */
    public async saveDataToNetwork(): Promise<TeamsInRegion> {
        return [];
    }
}
